import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  BarChart3, Users, CheckCircle, XCircle, TrendingUp, Clock,
  ArrowRight, Loader2, AlertCircle, MapPin, Package, LogOut,
  FileSpreadsheet, List
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 font-medium">{title}</p>
        <p className="text-3xl font-bold mt-1" style={{ color }}>{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
    </div>
  </div>
);

const FunnelBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-500">{value} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate('/admin6y5dv2x/login');
      } else {
        setError('Errore nel caricamento dei dati');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin6y5dv2x/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stocker-green" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
          <Button onClick={fetchDashboard} className="mt-4">Riprova</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-stocker-green rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">Geyser PRO Admin</h1>
              <p className="text-xs text-gray-500">Lead Management Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/admin6y5dv2x/leads')}
              className="flex items-center gap-2"
            >
              <List className="w-4 h-4" /> Lead
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin6y5dv2x/dealers')}
              className="flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" /> Rivenditori
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-gray-600 hover:text-red-600"
            >
              <LogOut className="w-4 h-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Lead totali"
            value={data?.total_leads || 0}
            icon={Users}
            color="#6BA43A"
          />
          <StatCard
            title="In lavorazione"
            value={data?.leads_in_corso || 0}
            icon={Clock}
            color="#F59E0B"
          />
          <StatCard
            title="Completati"
            value={data?.leads_inviato || 0}
            icon={CheckCircle}
            color="#10B981"
          />
          <StatCard
            title="Abbandonati"
            value={data?.leads_abbandonato || 0}
            icon={XCircle}
            color="#EF4444"
          />
        </div>

        {/* Funnel & Conversion */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Funnel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-stocker-green" />
              Analisi Funnel
            </h2>
            <div className="space-y-4">
              <FunnelBar
                label="Step 1 → 2 (Contatto + Mappa)"
                value={data?.funnel?.step2_reached || 0}
                total={data?.funnel?.step1_created || 1}
                color="#6BA43A"
              />
              <FunnelBar
                label="Step 2 → 3 (Kit selezionato)"
                value={data?.funnel?.step3_reached || 0}
                total={data?.funnel?.step2_reached || 1}
                color="#10B981"
              />
              <FunnelBar
                label="Step 3 → Inviato"
                value={data?.funnel?.sent_success || 0}
                total={data?.funnel?.step3_reached || 1}
                color="#3B82F6"
              />
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Conversione totale</span>
                <span className="text-2xl font-bold text-stocker-green">
                  {data?.funnel?.overall_conversion || 0}%
                </span>
              </div>
            </div>
          </div>

          {/* Top Kits */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-stocker-green" />
              Top Kits
            </h2>
            {data?.top_kits?.length > 0 ? (
              <div className="space-y-3">
                {data.top_kits.slice(0, 5).map((kit, idx) => (
                  <div key={kit.kit_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-stocker-green text-white text-xs flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-gray-900">{kit.kit_name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-gray-900">{kit.count}</span>
                      <span className="text-xs text-gray-500 ml-1">Lead</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nessuna selezione kit</p>
            )}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Kit consigliato selezionato</span>
                <span className="text-xl font-bold text-stocker-green">
                  {data?.recommended_rate || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Provinces */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-stocker-green" />
            Lead per provincia
          </h2>
          {data?.provinces?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {data.provinces.slice(0, 10).map((prov) => (
                <div key={prov.provincia} className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="font-medium text-gray-900 truncate">{prov.provincia}</p>
                  <p className="text-2xl font-bold text-stocker-green">{prov.count}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nessun lead</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
